import {
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  AfterContentInit,
  Output,
  QueryList,
  TemplateRef,
  OnDestroy,
} from '@angular/core';
import { isObservable, Observable, of, Subscriber } from 'rxjs';
import { take, filter } from 'rxjs/operators';

import { TabComponent } from './tab.component';

/**
 * A wrapper component around one or more `TabComponent`s. This handles selecting the active tab and displaying it's content
 */
@Component({
  selector: 'ps-ui-tab-group',
  templateUrl: './tab-group.component.html',
  styleUrls: ['./tab-group.component.scss'],
  exportAs: 'psUiTabGroup',
})
export class TabGroupComponent implements AfterContentInit, OnDestroy {
  /** Set the active tab to the specific index */
  @Input() get activeIndex() {
    return this._activeIndex;
  }
  set activeIndex(index: number) {
    const previousIndex = this._activeIndex;
    this._activeIndex = index;
    const tab = this.tabs?.get(index);
    if (!tab) {
      return;
    }
    if (tab.disabled) {
      this._activeIndex = previousIndex;
      return;
    }
    this.setActiveTab(tab, index);
  }
  private _activeIndex = 0;
  /** Emits the selected tabs index when it is selected */
  @Output() readonly activeIndexChange = new EventEmitter<number>();

  /** Optional callback function that can prevent changing tabs if it returns false */
  @Input() beforeTabChange: beforeTabChangeCallback = (
    currentIndex?: number,
    nextIndex?: number
  ) => true;

  @Input() tabAlignment: TabAlignment = 'center';

  getTabAlignment() {
    return `justify-content: ${this.tabAlignment}`;
  }

  @ContentChildren(TabComponent) tabs?: QueryList<TabComponent>;

  activeTabContent: TemplateRef<any> | null = null;

  private _subscriptions = new Subscriber();

  constructor() {}

  ngAfterContentInit(): void {
    const subscription = this.tabs?.changes.subscribe(() => {
      if (!this.tabs?.length) {
        return;
      }
      // The activeIndex input may not be a valid tab so reset to first tab in that case
      if (this._activeIndex > this.tabs.length) {
        this.activeIndex = 0;
      }
      const initialTab = this.tabs.get(this._activeIndex)!;
      // no tab preselected, _activeIndex == -1
      if (!initialTab) {
        return;
      }
      this.activeTabContent = initialTab.content;
    });

    this.tabs?.notifyOnChanges();
    this._subscriptions.add(subscription);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  setActiveTab(tab: TabComponent, index: number) {
    let canChangeTab = this.beforeTabChange(this.activeIndex, index);
    if (!isObservable(canChangeTab)) {
      canChangeTab = of(canChangeTab);
    }

    canChangeTab
      .pipe(
        take(1),
        filter((canChange) => canChange && !tab.disabled)
      )
      .subscribe(() => {
        this._activeIndex = index;
        this.activeTabContent = tab.content;
        this.activeIndexChange.emit(index);
      });
  }
}

type beforeTabChangeCallback = (
  currentIndex?: number,
  nextIndex?: number
) => boolean | Observable<boolean>;

type TabAlignment = 'left' | 'center' | 'right';
